import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Select, message, Modal, Form, InputNumber, Image } from 'antd';
import { StopOutlined, ClockCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { confirm } = Modal;

interface Server {
  id: number;
  name: string;
  username: string;
  game_name: string;
  game_icon?: string;
  node_name: string;
  status: string;
  port: number;
  max_players: number;
  expires_at: string;
  created_at: string;
}

const AdminServers: React.FC = () => {
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadServers();
  }, [statusFilter]);

  const loadServers = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/admin/servers?${params.toString()}`);
      setServers(response.data.servers || []);
    } catch (error: any) {
      console.error('加载服务器列表失败:', error);
      message.error(`加载服务器列表失败: ${error.response?.data?.message || error.message || '未知错误'}`);
      setServers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleForceStop = async (serverId: number) => {
    try {
      await api.post(`/admin/servers/${serverId}/force-stop`);
      message.success('服务器已强制停止');
      loadServers();
    } catch (error: any) {
      message.error(error.response?.data?.message || '强制停止失败');
    }
  };

  const handleExtendTime = async (values: { days: number }) => {
    if (!selectedServer) return;
    
    try {
      await api.post(`/admin/servers/${selectedServer.id}/extend`, values);
      message.success(`服务器时间已延长 ${values.days} 天`);
      setExtendModalVisible(false);
      setSelectedServer(null);
      loadServers();
    } catch (error: any) {
      message.error(error.response?.data?.message || '延长时间失败');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      running: { color: 'green', text: '运行中' },
      stopped: { color: 'red', text: '已停止' },
      starting: { color: 'orange', text: '启动中' },
      restarting: { color: 'orange', text: '重启中' },
      installing: { color: 'blue', text: '安装中' },
      error: { color: 'red', text: '错误' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '服务器名',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '所有者',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '游戏类型',
      dataIndex: 'game_name',
      key: 'game_name',
      width: 150,
      render: (gameName: string, record: Server) => (
        <div className="flex items-center space-x-2">
          <Image
            src={record.game_icon || '/images/game-icons/placeholder_184x69.jpg'}
            alt={gameName}
            width={24}
            height={18}
            style={{ objectFit: 'cover', borderRadius: '3px' }}
            fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAyNCAxOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjE4IiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjEyIiB5PSI5IiBmb250LmZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdhbWU8L3RleHQ+Cjwvc3ZnPg=="
          />
          <span>{gameName}</span>
        </div>
      ),
    },
    {
      title: '节点',
      dataIndex: 'node_name',
      key: 'node_name',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '端口',
      dataIndex: 'port',
      key: 'port',
      width: 80,
    },
    {
      title: '最大玩家',
      dataIndex: 'max_players',
      key: 'max_players',
      width: 100,
    },
    {
      title: '到期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 150,
      render: (date: string) => {
        const expireDate = new Date(date);
        const now = new Date();
        const isExpired = expireDate < now;
        return (
          <span className={isExpired ? 'text-red-500' : ''}>
            {expireDate.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 190,
      render: (_: any, record: Server) => (
        <div className="space-x-2">
          {record.status === 'running' && (
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => {
                confirm({
                  title: '确认强制停止服务器？',
                  content: `确定要强制停止服务器 ${record.name} 吗？`,
                  onOk: () => handleForceStop(record.id),
                });
              }}
            >
              强停
            </Button>
          )}
          
          <Button
            size="small"
            icon={<ClockCircleOutlined />}
            onClick={() => {
              setSelectedServer(record);
              setExtendModalVisible(true);
            }}
          >
            延时
          </Button>

          <Button
            size="small"
            type="primary"
            onClick={() => navigate(`/servers/${record.id}`)}
          >
            管理
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="fade-in">
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">服务器管理</h2>
        </div>

        <div className="mb-6">
          <Select
            placeholder="服务器状态"
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-32"
            allowClear
          >
            <Option value="running">运行中</Option>
            <Option value="stopped">已停止</Option>
            <Option value="starting">启动中</Option>
            <Option value="installing">安装中</Option>
            <Option value="error">错误</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={servers}
          rowKey="id"
          loading={loading}
          className="admin-server-list-table"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个服务器`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 延长时间模态框 */}
      <Modal
        title={`延长服务器 ${selectedServer?.name} 时间`}
        open={extendModalVisible}
        onCancel={() => {
          setExtendModalVisible(false);
          setSelectedServer(null);
        }}
        footer={null}
      >
        <Form onFinish={handleExtendTime} layout="vertical">
          <Form.Item
            name="days"
            label="延长天数"
            rules={[{ required: true, message: '请输入延长天数' }]}
          >
            <InputNumber
              min={1}
              max={365}
              placeholder="输入延长天数"
              className="w-full"
              addonAfter="天"
            />
          </Form.Item>

          <div className="bg-blue-50 p-3 rounded mb-4">
            <p className="text-sm text-blue-600">
              延长时间将从当前到期时间开始计算，这是管理员免费操作
            </p>
          </div>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => {
              setExtendModalVisible(false);
              setSelectedServer(null);
            }}>
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              确认延长
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminServers;



